import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { Conversation } from '../types';
import {
  MessageSquare,
  Clock,
  CheckCircle,
  Activity,
  Phone,
  RefreshCw,
  Plus,
  FileText,
  List,
  Trash2,
  AlertCircle,
  X,
  XOctagon,
  ChevronDown,
  Search,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toZonedTime } from 'date-fns-tz';
import { ConversationDetail } from './ConversationDetail';
import { CreateTemplateModal } from '../components/CreateTemplateModal';
import { StartConversationModal } from '../components/StartConversationModal';
import { TemplatesListModal } from '../components/TemplatesListModal';

type ConversationStatus = 'WAITING' | 'MENU' | 'QUEUED' | 'ACTIVE' | 'CLOSED' | 'AVALIACAO';

export const ConversationsLayout: React.FC = () => {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();
  const { isAdmin, isDepartmentManager, agent } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [isClosingAll, setIsClosingAll] = useState(false);
  const [filter, setFilter] = useState<'all' | 'assigned' | 'unassigned'>('all');
  const [statusFilter, setStatusFilter] = useState<ConversationStatus | 'all'>('ACTIVE');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [departments, setDepartments] = useState<Array<{ id: string; name: string }>>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Read status filter from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const statusParam = params.get('status');
    if (statusParam && ['WAITING', 'MENU', 'QUEUED', 'ACTIVE', 'CLOSED', 'AVALIACAO', 'all'].includes(statusParam)) {
      setStatusFilter(statusParam as ConversationStatus | 'all');
    }
  }, []);
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
  const [showStartConversationModal, setShowStartConversationModal] = useState(false);
  const [showTemplatesListModal, setShowTemplatesListModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteResultModal, setShowDeleteResultModal] = useState(false);
  const [deleteResult, setDeleteResult] = useState<{ success: boolean; message: string } | null>(null);
  const [conversationToDelete, setConversationToDelete] = useState<Conversation | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedConversationIds, setSelectedConversationIds] = useState<Set<string>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  // Use refs to store current filter values so WebSocket can access them
  const currentFiltersRef = useRef({ statusFilter, filter, departmentFilter, searchFilter });
  const loadConversationsRef = useRef<((showRefreshing?: boolean) => Promise<void>) | null>(null);

  // Format date/time in São Paulo timezone
  const formatSaoPauloDateTime = (date: Date | string): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const saoPauloTime = toZonedTime(dateObj, 'America/Sao_Paulo');
    return format(saoPauloTime, 'dd/MM/yyyy HH:mm', { locale: ptBR });
  };

  // Update refs whenever filters change
  useEffect(() => {
    currentFiltersRef.current = { statusFilter, filter, departmentFilter, searchFilter };
  }, [statusFilter, filter, departmentFilter, searchFilter]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, statusFilter, departmentFilter, searchFilter]);

  // Clear search when other filters change
  useEffect(() => {
    setSearchFilter('');
  }, [filter, statusFilter, departmentFilter]);

  const loadDepartments = async () => {
    try {
      const data = await api.getDepartments();
      setDepartments(data);
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  };

  const loadConversations = useCallback(async (showRefreshing = false) => {
    try {
      if (showRefreshing) {
        setIsRefreshing(true);
      } else if (!isLoading) {
        setIsLoadingPage(true);
      }

      // Always use current filter values from ref
      const { statusFilter: currentStatus, filter: currentFilter, departmentFilter: currentDept, searchFilter: currentSearch } = currentFiltersRef.current;

      const params: any = {
        page: String(currentPage),
        limit: '10',
      };

      // Always send status parameter to differentiate between "no filter" and "all statuses"
      params.status = currentStatus;

      if (currentFilter === 'assigned') {
        params.assigned = 'true';
      } else if (currentFilter === 'unassigned') {
        params.assigned = 'false';
      }

      if (currentDept !== 'all') {
        params.departmentId = currentDept;
      }

      if (currentSearch && currentSearch.trim() !== '') {
        params.search = currentSearch.trim();
      }

      // When refreshing (triggered by WebSocket or manual refresh), skip cache to get fresh data
      const skipCache = showRefreshing;
      console.log('🔍 [ConversationsLayout] Loading with params:', params, 'skipCache:', skipCache);
      const response = await api.getConversations(params, skipCache);
      console.log('📦 [ConversationsLayout] Response received:', response?.data?.conversations?.length || 0, 'conversations');

      // Only update if we got valid data
      if (response && response.data && response.data.conversations) {
        console.log('✅ [ConversationsLayout] Updating conversations list with', response.data.conversations.length, 'items');
        setConversations(response.data.conversations);
        setTotalPages(response.data.pagination.totalPages);
        setTotal(response.data.pagination.total);
      } else {
        console.warn('⚠️ [ConversationsLayout] Received invalid response, keeping current conversations');
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
      // Don't clear the list on error, keep showing current data
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
      setIsLoadingPage(false);
    }
  }, [currentPage, isLoading]);

  // Update loadConversations ref
  useEffect(() => {
    loadConversationsRef.current = loadConversations;
  }, [loadConversations]);

  // Load conversations when filters or page change
  useEffect(() => {
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, statusFilter, departmentFilter, searchFilter, currentPage]);

  // Load departments for filter
  useEffect(() => {
    if (isAdmin()) {
      loadDepartments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh list when conversation is opened (to update unread count)
  useEffect(() => {
    if (conversationId) {
      // Small delay to allow backend to mark messages as read
      const timer = setTimeout(() => {
        loadConversations(true);
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // WebSocket for real-time updates
  useEffect(() => {
    console.log('🔌 [ConversationsLayout] Initializing WebSocket connection...');
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    console.log('🔌 [ConversationsLayout] API URL:', apiUrl);

    const socket = io(apiUrl, {
      transports: ['polling', 'websocket'],
      upgrade: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('✅ [ConversationsLayout] WebSocket connected, socket ID:', socket.id);
    });

    socket.on('conversation-updated', (data: { conversationId: string; messageId?: string; senderType?: string; action?: string }) => {
      console.log('📩 [ConversationsLayout] Conversation updated via WebSocket:', data);
      console.log('📊 [ConversationsLayout] Current conversations count before reload:', conversations.length);

      // Use ref to get current filter values (avoids stale closure)
      const { statusFilter: currentStatus, filter: currentFilter, departmentFilter: currentDept } = currentFiltersRef.current;
      console.log('🎯 [ConversationsLayout] Current filters - status:', currentStatus, 'filter:', currentFilter, 'dept:', currentDept);

      // Reload conversations to update unread counts and status
      console.log('🔄 [ConversationsLayout] Reloading conversations list...');
      if (loadConversationsRef.current) {
        loadConversationsRef.current(true);
      }
    });

    socket.on('disconnect', (reason) => {
      console.log('❌ [ConversationsLayout] WebSocket disconnected, reason:', reason);
    });

    socket.on('connect_error', (error) => {
      console.error('❌ [ConversationsLayout] WebSocket connection error:', error);
    });

    return () => {
      console.log('🧹 [ConversationsLayout] Cleanup: Disconnecting WebSocket');
      socket.disconnect();
    };
  }, []);

  const getStatusColor = (status: ConversationStatus) => {
    switch (status) {
      case 'WAITING':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'MENU':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'QUEUED':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'AVALIACAO':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'CLOSED':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusIcon = (status: ConversationStatus) => {
    switch (status) {
      case 'WAITING':
        return <Clock className="w-3 h-3" />;
      case 'MENU':
        return <MessageSquare className="w-3 h-3" />;
      case 'QUEUED':
        return <Clock className="w-3 h-3" />;
      case 'ACTIVE':
        return <Activity className="w-3 h-3" />;
      case 'AVALIACAO':
        return <FileText className="w-3 h-3" />;
      case 'CLOSED':
        return <CheckCircle className="w-3 h-3" />;
      default:
        return <MessageSquare className="w-3 h-3" />;
    }
  };

  const handleDeleteClick = (conversation: Conversation, e: React.MouseEvent) => {
    e.stopPropagation();
    setConversationToDelete(conversation);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!conversationToDelete) return;

    setIsDeleting(true);
    try {
      await api.deleteConversation(conversationToDelete.id);
      setDeleteResult({
        success: true,
        message: 'Conversa excluída com sucesso!'
      });
      setShowDeleteModal(false);
      setShowDeleteResultModal(true);

      // Remove from selection if it was selected
      setSelectedConversationIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(conversationToDelete.id);
        return newSet;
      });

      // Reload conversations after deletion
      await loadConversations(true);

      // If the deleted conversation was selected, navigate back to list
      if (conversationId === conversationToDelete.id) {
        navigate('/conversations');
      }
    } catch (error: any) {
      setDeleteResult({
        success: false,
        message: error.response?.data?.error || 'Erro ao excluir conversa'
      });
      setShowDeleteModal(false);
      setShowDeleteResultModal(true);
    } finally {
      setIsDeleting(false);
      setConversationToDelete(null);
    }
  };

  const toggleSelectConversation = (id: string) => {
    setSelectedConversationIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedConversationIds.size === conversations.length) {
      setSelectedConversationIds(new Set());
    } else {
      setSelectedConversationIds(new Set(conversations.map(c => c.id)));
    }
  };

  const handleCloseAll = async () => {
    const confirmMessage = 'Tem certeza que deseja encerrar TODAS as conversas ativas? Esta ação não pode ser desfeita.';
    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      setIsClosingAll(true);
      const result = await api.closeAllConversations();
      alert(result.message);
      await loadConversations(true);
    } catch (error) {
      console.error('Failed to close all conversations:', error);
      alert('Erro ao encerrar conversas. Por favor, tente novamente.');
    } finally {
      setIsClosingAll(false);
    }
  };

  const handleBulkDelete = async () => {
    setIsBulkDeleting(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const id of selectedConversationIds) {
        try {
          await api.deleteConversation(id);
          successCount++;
        } catch (error) {
          errorCount++;
        }
      }

      setDeleteResult({
        success: errorCount === 0,
        message: errorCount === 0
          ? `${successCount} conversa(s) excluída(s) com sucesso!`
          : `${successCount} excluída(s), ${errorCount} erro(s)`
      });
      setShowBulkDeleteModal(false);
      setShowDeleteResultModal(true);
      setSelectedConversationIds(new Set());

      // Reload conversations after deletion
      await loadConversations(true);

      // If the selected conversation was deleted, navigate back
      if (conversationId && selectedConversationIds.has(conversationId)) {
        navigate('/conversations');
      }
    } finally {
      setIsBulkDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] overflow-hidden">
      {/* Left Sidebar - Conversations List */}
      <div className={`${conversationId ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-96 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800`}>
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Conversas</h2>
            <button
              onClick={() => loadConversations(true)}
              disabled={isRefreshing}
              className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
              title="Atualizar"
            >
              <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Bulk Actions Bar */}
          {conversations.length > 0 && (isAdmin() || isDepartmentManager() || agent?.role === 'GENERAL_MANAGER') && (
            <div className="mb-3 flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer hover:text-gray-900 dark:hover:text-white transition">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={selectedConversationIds.size === conversations.length && conversations.length > 0}
                    onChange={toggleSelectAll}
                    className="peer sr-only"
                  />
                  <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-500 rounded peer-checked:bg-blue-600 peer-checked:border-blue-600 flex items-center justify-center transition-all">
                    {selectedConversationIds.size === conversations.length && conversations.length > 0 && (
                      <svg className="w-3 h-3 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" viewBox="0 0 24 24" stroke="currentColor">
                        <path d="M5 13l4 4L19 7"></path>
                      </svg>
                    )}
                  </div>
                </div>
                <span className="font-medium">
                  {selectedConversationIds.size > 0
                    ? `${selectedConversationIds.size} selecionada(s)`
                    : 'Selecionar todas'}
                </span>
              </label>
              {selectedConversationIds.size > 0 && (
                <button
                  onClick={() => setShowBulkDeleteModal(true)}
                  className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition flex items-center gap-1.5 shadow-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="font-medium">Excluir</span>
                </button>
              )}
            </div>
          )}

          {/* Action Buttons - Dropdown */}
          <div className="mb-3 relative">
            <button
              onClick={() => setShowActionsDropdown(!showActionsDropdown)}
              className="w-full px-3 py-2 bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600 text-white rounded-lg transition flex items-center justify-center gap-2 text-sm"
            >
              <Plus className="w-4 h-4" />
              <span>Ações</span>
              <ChevronDown className="w-4 h-4 ml-auto" />
            </button>
            {showActionsDropdown && (
              <div className="absolute left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
                <button
                  onClick={() => {
                    setShowStartConversationModal(true);
                    setShowActionsDropdown(false);
                  }}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-700 dark:text-gray-300 text-sm"
                >
                  <Plus className="w-4 h-4 text-green-600" />
                  <span>Iniciar Conversa</span>
                </button>
                <button
                  onClick={() => {
                    setShowTemplatesListModal(true);
                    setShowActionsDropdown(false);
                  }}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-700 dark:text-gray-300 text-sm"
                >
                  <List className="w-4 h-4 text-indigo-600" />
                  <span>Ver Templates</span>
                </button>
                {agent?.role === 'ADMIN' && (
                  <button
                    onClick={() => {
                      setShowCreateTemplateModal(true);
                      setShowActionsDropdown(false);
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-700 dark:text-gray-300 text-sm"
                  >
                    <FileText className="w-4 h-4 text-purple-600" />
                    <span>Criar Template</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    handleCloseAll();
                    setShowActionsDropdown(false);
                  }}
                  disabled={isClosingAll}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  <XOctagon className={`w-4 h-4 text-red-600 ${isClosingAll ? 'animate-pulse' : ''}`} />
                  <span>{isClosingAll ? 'Encerrando...' : 'Encerrar Todas'}</span>
                </button>
              </div>
            )}
          </div>

          {/* Filters - Compact */}
          <div className="space-y-1.5">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ConversationStatus | 'all')}
              className="w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs"
            >
              <option value="all">📊 Todos Status</option>
              <option value="WAITING">⏰ Aguardando</option>
              <option value="MENU">📱 Menu</option>
              <option value="QUEUED">🔄 Fila</option>
              <option value="ACTIVE">✅ Ativo</option>
              {(agent?.role === 'ADMIN' || agent?.role === 'GENERAL_MANAGER') && (
                <option value="AVALIACAO">📊 Avaliação</option>
              )}
              <option value="CLOSED">🔒 Fechado</option>
            </select>

            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as 'all' | 'assigned' | 'unassigned')}
              className="w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs"
            >
              <option value="all">📋 Todas</option>
              <option value="assigned">✅ Minhas</option>
              <option value="unassigned">⏳ Não Atribuídas</option>
            </select>

            {isAdmin() && departments.length > 0 && (
              <select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                className="w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs"
              >
                <option value="all">🏢 Todos Depts</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            )}

            {/* Search Filter */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Buscar por nome ou telefone"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                translate="no"
                className="w-full pl-8 pr-8 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs placeholder:text-gray-400"
              />
              {searchFilter && (
                <button
                  onClick={() => setSearchFilter('')}
                  className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading || isLoadingPage ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-500 dark:text-gray-400">Carregando atendimentos...</p>
            </div>
          ) : conversations.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nenhuma conversa encontrada</p>
            </div>
          ) : (
            <div className={`divide-y divide-gray-200 dark:divide-gray-700 relative ${isLoadingPage ? 'opacity-50 pointer-events-none' : ''}`}>
              {isLoadingPage && (
                <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/50 dark:bg-gray-800/50">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              )}
              {conversations.map((conversation) => (
                <div
                  key={conversation.id}
                  onClick={() => navigate(`/conversations/${conversation.id}`)}
                  className={`p-5 cursor-pointer transition relative group ${
                    conversationId === conversation.id
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    {(isAdmin() || isDepartmentManager() || agent?.role === 'GENERAL_MANAGER') && (
                      <div className="mr-3 pt-1" onClick={(e) => e.stopPropagation()}>
                        <label className="relative cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedConversationIds.has(conversation.id)}
                            onChange={() => toggleSelectConversation(conversation.id)}
                            className="peer sr-only"
                          />
                          <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-500 rounded peer-checked:bg-blue-600 peer-checked:border-blue-600 flex items-center justify-center transition-all hover:border-blue-500 dark:hover:border-blue-400">
                            {selectedConversationIds.has(conversation.id) && (
                              <svg className="w-3 h-3 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" viewBox="0 0 24 24" stroke="currentColor">
                                <path d="M5 13l4 4L19 7"></path>
                              </svg>
                            )}
                          </div>
                        </label>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-base text-gray-900 dark:text-white truncate">
                          {conversation.customerName || conversation.customerPhone}
                        </h3>
                        {conversation.unreadCount && conversation.unreadCount > 0 && (
                          <div className="flex items-center justify-center min-w-[22px] h-6 px-2 bg-green-500 text-white rounded-full text-xs font-bold">
                            {conversation.unreadCount}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(conversation.status)}`}>
                          {getStatusIcon(conversation.status)}
                          <span>{conversation.status}</span>
                        </span>
                        {conversation.contact?.tags && conversation.contact.tags.length > 0 && (
                          conversation.contact.tags.slice(0, 2).map((tag) => {
                            const tagColor = 'color' in tag ? tag.color : tag.tag?.color;
                            const tagName = 'name' in tag ? tag.name : tag.tag?.name;
                            return (
                              <span
                                key={tag.id}
                                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium text-white"
                                style={{ backgroundColor: tagColor }}
                              >
                                {tagName}
                              </span>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mt-2">
                    <div className="flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5" />
                      <span>{conversation.customerPhone}</span>
                    </div>
                    <span className="text-xs">
                      {formatSaoPauloDateTime(conversation.updatedAt)}
                    </span>
                  </div>

                  {/* Delete Button - Only visible for admins and department managers (from their own department) */}
                  {(isAdmin() || (isDepartmentManager() && conversation.departmentId === agent?.department?.id)) && (
                    <button
                      onClick={(e) => handleDeleteClick(conversation, e)}
                      className="absolute top-2 right-2 p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition opacity-0 group-hover:opacity-100"
                      title="Excluir conversa"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700 dark:text-gray-300">
                {conversations.length} de {total}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  translate="no"
                >
                  Anterior
                </button>
                <span className="text-gray-700 dark:text-gray-300" translate="no">
                  {currentPage}/{totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  translate="no"
                >
                  Próxima
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Right Panel - Conversation Detail */}
      <div className={`${conversationId ? 'flex' : 'hidden md:flex'} flex-1 flex-col bg-gray-50 dark:bg-gray-900`}>
        {conversationId ? (
          <ConversationDetail />
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400">
            <div className="text-center">
              <MessageSquare className="w-24 h-24 mx-auto mb-4 opacity-20" />
              <p className="text-xl font-medium">Selecione uma conversa</p>
              <p className="text-sm mt-2">Escolha uma conversa da lista para começar</p>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <TemplatesListModal
        isOpen={showTemplatesListModal}
        onClose={() => setShowTemplatesListModal(false)}
      />
      <CreateTemplateModal
        isOpen={showCreateTemplateModal}
        onClose={() => setShowCreateTemplateModal(false)}
        onSuccess={() => {
          // Success feedback is handled by the modal itself
        }}
      />
      <StartConversationModal
        isOpen={showStartConversationModal}
        onClose={() => setShowStartConversationModal(false)}
        onSuccess={() => {
          loadConversations(true);
        }}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && conversationToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Confirmar Exclusão
              </h3>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setConversationToDelete(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                disabled={isDeleting}
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Tem certeza que deseja excluir a conversa com{' '}
              <strong>{conversationToDelete.customerName || conversationToDelete.customerPhone}</strong>?
              <br />
              <br />
              Esta ação não pode ser desfeita e todos os dados relacionados serão permanentemente removidos.
            </p>
            {isDeleting && (
              <div className="mb-4 flex items-center justify-center space-x-2 text-blue-600 dark:text-blue-400">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <span className="text-sm">Excluindo conversa...</span>
              </div>
            )}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setConversationToDelete(null);
                }}
                disabled={isDeleting}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Excluindo...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    <span>Excluir</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="ml-4 text-lg font-semibold text-gray-900 dark:text-white">Confirmar Exclusão em Massa</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Você está prestes a excluir <strong>{selectedConversationIds.size}</strong> conversa(s).
              <br />
              <br />
              Esta ação não pode ser desfeita e todos os dados relacionados serão permanentemente removidos.
            </p>
            {isBulkDeleting && (
              <div className="mb-4 flex items-center justify-center space-x-2 text-blue-600 dark:text-blue-400">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <span className="text-sm">Excluindo conversas...</span>
              </div>
            )}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={isBulkDeleting}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={isBulkDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isBulkDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Excluindo...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    <span>Excluir Todas</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Result Modal */}
      {showDeleteResultModal && deleteResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-center mb-4">
              <div className={`w-12 h-12 ${deleteResult.success ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'} rounded-full flex items-center justify-center`}>
                {deleteResult.success ? (
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                )}
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 text-center">
              {deleteResult.success ? 'Sucesso' : 'Erro'}
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6 text-center">
              {deleteResult.message}
            </p>
            <div className="flex justify-center">
              <button
                onClick={() => {
                  setShowDeleteResultModal(false);
                  setDeleteResult(null);
                }}
                className={`px-6 py-2 ${deleteResult.success ? 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600' : 'bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600'} text-white rounded-lg transition`}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
